<template>
  <div class="w-full px-6 py-6">
    <div class="flex items-start sm:items-center justify-between mb-4 flex-wrap gap-3">
      <div>
        <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Administration</div>
        <h1 class="text-2xl font-semibold dark:text-gray-100">Users</h1>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
        <button @click="exportCsv" class="px-4 py-2 rounded-md text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100">
          Export CSV
        </button>
        <button @click="newUserOpen = true" class="px-4 py-2 rounded-md text-sm bg-yellow-500 hover:bg-yellow-600 text-white font-medium">
          + New user
        </button>
      </div>
    </div>

    <div class="w-full">
      <!-- Filters moved above the table so the table can use the full width -->
      <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-3 shadow-sm mb-4">
        <div class="flex flex-wrap items-end gap-4 text-sm">
          <div>
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Status</label>
            <div class="flex gap-2 flex-wrap">
              <button v-for="opt in statusOptions" :key="opt.value"
                @click="setStatus(opt.value)"
                :class="['px-2.5 py-1 rounded-full text-xs', status === opt.value ? 'bg-yellow-500 text-white' : 'bg-gray-100 dark:bg-gray-600 dark:text-gray-100']">
                {{ opt.label }}
              </button>
            </div>
          </div>
          <div>
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">User type</label>
            <div class="flex gap-2 flex-wrap">
              <button v-for="opt in scopeOptions" :key="opt.value"
                @click="setScope(opt.value)"
                :class="['px-2.5 py-1 rounded-full text-xs', scope === opt.value ? 'bg-yellow-500 text-white' : 'bg-gray-100 dark:bg-gray-600 dark:text-gray-100']">
                {{ opt.label }}
              </button>
            </div>
          </div>
          <div v-click-outside="closeCompanyDropdown" class="min-w-[220px]">
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Company</label>
            <div class="relative">
              <input type="text" v-model="companySearch"
                @focus="companyDropdownOpen = true; companyHighlight = -1"
                @keydown.down.prevent="onCompanyKey('down')"
                @keydown.up.prevent="onCompanyKey('up')"
                @keydown.enter.prevent="onCompanyEnter"
                @keydown.esc.prevent="closeCompanyDropdown"
                class="w-full px-3 py-2 pr-7 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 text-sm"
                placeholder="All companies" />
              <button v-if="companyId || companySearch" @click="clearCompany"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500" title="Clear">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
              <ul v-if="companyDropdownOpen && filteredCompanies.length" ref="companyList"
                class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow">
                <li v-for="(c, i) in filteredCompanies" :key="c.id"
                  @mousedown.prevent="pickCompany(c)"
                  @mouseenter="companyHighlight = i"
                  :class="['px-3 py-2 text-sm cursor-pointer dark:text-gray-100',
                    companyHighlight === i ? 'bg-yellow-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700']">
                  {{ c.companyname }}
                </li>
              </ul>
              <div v-else-if="companyDropdownOpen && companyOptions.length && !filteredCompanies.length"
                class="absolute z-10 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow px-3 py-2 text-xs text-gray-500">
                No companies match.
              </div>
            </div>
          </div>
          <div class="min-w-[180px]">
            <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Role</label>
            <select v-model="roleId" @change="onRoleChange"
              class="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 text-sm">
              <option :value="null">All roles</option>
              <option v-for="r in roleOptions" :key="r.id" :value="r.id">{{ r.name }} ({{ r.type }})</option>
            </select>
          </div>
          <div class="ml-auto">
            <button @click="clearFilters" class="text-xs text-gray-500 hover:underline">Clear all</button>
          </div>
        </div>
      </div>

      <div class="w-full">
        <div v-if="activeChips.length" class="flex items-center gap-2 mb-3 flex-wrap">
          <span class="text-xs text-gray-500">Applied:</span>
          <span v-for="chip in activeChips" :key="chip.key" class="bg-yellow-500 text-white rounded-full px-3 py-1 text-xs flex items-center gap-1">
            {{ chip.label }}
            <button @click="chip.clear" class="ml-1 hover:text-yellow-200">&times;</button>
          </span>
        </div>

        <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-sm overflow-hidden">
          <div class="p-3 border-b border-gray-200 dark:border-gray-600 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div class="relative w-full md:max-w-sm">
              <input v-model="search" @input="debouncedSearch"
                class="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 text-sm"
                placeholder="Search users — name, email, phone, title, or ID…" />
              <svg class="w-4 h-4 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
              </svg>
            </div>
            <div class="flex items-center gap-3 md:justify-end">
              <span class="text-xs text-gray-500 dark:text-gray-300 whitespace-nowrap">{{ total }} users</span>
              <ul v-if="rows.length && lastPage > 1" class="inline-flex items-stretch -space-x-px">
                <li>
                  <button @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1"
                    class="flex items-center justify-center h-full py-1.5 px-2.5 ml-0 text-gray-500 bg-white rounded-l-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-40">
                    <span class="sr-only">Previous</span>
                    <svg class="w-4 h-4" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  </button>
                </li>
                <li v-for="(pageNum, idx) in visiblePages" :key="idx">
                  <span v-if="pageNum === '...'" class="flex items-center justify-center text-sm py-1.5 px-3 leading-tight text-gray-400 bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700">…</span>
                  <button v-else @click="goToPage(pageNum)"
                    :class="pageNum === currentPage ? 'bg-yellow-50 dark:bg-gray-700 text-yellow-600 dark:text-white font-semibold' : 'hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white'"
                    class="flex items-center justify-center text-sm py-1.5 px-3 leading-tight text-gray-500 bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                    {{ pageNum }}
                  </button>
                </li>
                <li>
                  <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= lastPage"
                    class="flex items-center justify-center h-full py-1.5 px-2.5 leading-tight text-gray-500 bg-white rounded-r-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-40">
                    <span class="sr-only">Next</span>
                    <svg class="w-4 h-4" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/></svg>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="!loading && !rows.length" class="text-center py-12 text-gray-500 dark:text-gray-400">No users match the filters.</div>
          <div v-if="rows.length" class="overflow-x-auto">
          <table class="w-full text-sm min-w-[640px]">
            <thead class="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-700 dark:text-gray-300">
              <tr>
                <th class="text-left px-4 py-3">Name</th>
                <th class="text-left px-4 py-3">Email</th>
                <th class="text-left px-4 py-3">Phone</th>
                <th class="text-left px-4 py-3">Roles</th>
                <th class="text-left px-4 py-3">User type</th>
                <th class="text-left px-4 py-3">Source</th>
                <th class="text-left px-4 py-3">Scope</th>
                <th class="text-left px-4 py-3">Last login</th>
                <th class="text-left px-4 py-3">Active</th>
                <th class="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
              <tr v-for="u in rows" :key="u.id" class="hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-100">
                <td class="px-4 py-3 font-medium">
                  <router-link :to="`/admin/users/${u.id}`" class="hover:underline">
                    {{ displayName(u) }}
                  </router-link>
                </td>
                <td class="px-4 py-3">{{ u.email }}</td>
                <td class="px-4 py-3">
                  <div v-if="u.phone_primary" class="whitespace-nowrap">
                    <a :href="telLink(u.phone_primary, u.phone_primary_ext)" class="text-blue-600 dark:text-blue-400 hover:underline">
                      {{ fmtPhone(u.phone_primary) }}<span v-if="u.phone_primary_ext" class="text-gray-500"> x{{ u.phone_primary_ext }}</span>
                    </a>
                    <span class="text-xs text-gray-500"> (primary)</span>
                  </div>
                  <div v-if="u.phone_secondary" class="whitespace-nowrap">
                    <a :href="telLink(u.phone_secondary, u.phone_secondary_ext)" class="text-blue-600 dark:text-blue-400 hover:underline">
                      {{ fmtPhone(u.phone_secondary) }}<span v-if="u.phone_secondary_ext" class="text-gray-500"> x{{ u.phone_secondary_ext }}</span>
                    </a>
                    <span class="text-xs text-gray-500"> (secondary)</span>
                  </div>
                  <span v-if="!u.phone_primary && !u.phone_secondary" class="text-gray-400">—</span>
                </td>
                <td class="px-4 py-3">
                  <div class="flex flex-wrap gap-1 max-w-[220px]">
                    <span v-for="r in (u.roles || [])" :key="r.id"
                      :class="r.type === 'Internal' ? 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-100' : (r.type === 'External' ? 'bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-100' : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-100')"
                      class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap" :title="`${r.name} (${r.type})`">
                      {{ r.name }}
                    </span>
                    <span v-if="!(u.roles && u.roles.length)" class="text-gray-400 text-xs">—</span>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div v-if="canOverrideUserType" class="flex items-center gap-2">
                    <select :value="u.user_type" @change="handleUserTypeChange(u, $event)"
                      class="border border-gray-300 dark:border-gray-600 rounded-lg py-1 px-2 bg-white dark:bg-gray-700 dark:text-gray-100 text-sm"
                      :class="u.user_type_override_permanent == 1 ? 'font-semibold border-green-600' : ''">
                      <option v-for="opt in userTypeChoices" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                    </select>
                    <span v-if="u.user_type_overridden == 1" class="text-xs italic text-red-500">manual</span>
                  </div>
                  <div v-else>
                    {{ u.user_type || '—' }}
                    <span v-if="u.user_type_overridden == 1" class="text-xs italic text-red-500">(manual)</span>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <span :class="isAzureSourced(u) ? 'bg-sky-100 text-sky-700 dark:bg-sky-800 dark:text-sky-100' : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200'" class="inline-block text-xs px-2 py-0.5 rounded-full">
                    {{ isAzureSourced(u) ? 'Azure' : 'Manual' }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span :class="u.is_internal ? 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-100' : 'bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-100'" class="inline-block text-xs px-2 py-0.5 rounded-full">
                    {{ u.is_internal ? 'Internal' : 'External' }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">{{ formatDateTime(u.lastportallogin) }}</td>
                <td class="px-4 py-3">
                  <label class="inline-flex items-center"
                    :class="canManageUser(u) ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'"
                    :title="!canManageUser(u) ? 'You cannot change the status of a user whose role is more senior than yours.' : (u.retired ? 'Inactive — click to enable' : 'Active — click to disable')">
                    <input type="checkbox" :checked="!u.retired" :disabled="togglingActiveId === u.id || !canManageUser(u)"
                      @change="toggleActive(u, $event.target.checked)" class="sr-only peer">
                    <div class="w-11 h-6 bg-gray-200 dark:bg-gray-500 rounded-full peer peer-checked:bg-green-500 peer-disabled:opacity-50 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                    <span class="ml-2 text-xs" :class="u.retired ? 'text-gray-500' : 'text-green-700 dark:text-green-300'">{{ u.retired ? 'Inactive' : 'Active' }}</span>
                  </label>
                </td>
                <td class="px-4 py-3">
                  <div class="flex flex-col sm:flex-row sm:justify-end gap-2">
                    <button @click="openHistory(u.id)"
                      class="inline-flex items-center justify-center px-3 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-500 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 transition">
                      History
                    </button>
                    <router-link :to="`/admin/users/${u.id}`"
                      class="inline-flex items-center justify-center px-3 py-1 text-xs rounded-md bg-yellow-500 hover:bg-yellow-600 text-white font-medium transition">
                      Edit
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          <div v-if="rows.length" class="p-3 border-t border-gray-200 dark:border-gray-600 flex flex-col md:flex-row items-start md:items-center justify-between text-xs text-gray-600 dark:text-gray-300 gap-3">
            <span>Page {{ currentPage }} of {{ lastPage }} — {{ total }} total</span>
            <ul v-if="lastPage > 1" class="inline-flex items-stretch -space-x-px">
              <li>
                <button @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1"
                  class="flex items-center justify-center h-full py-1.5 px-2.5 ml-0 text-gray-500 bg-white rounded-l-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-40">
                  <span class="sr-only">Previous</span>
                  <svg class="w-4 h-4" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                </button>
              </li>
              <li v-for="(pageNum, idx) in visiblePages" :key="idx">
                <span v-if="pageNum === '...'" class="flex items-center justify-center text-sm py-1.5 px-3 leading-tight text-gray-400 bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700">…</span>
                <button v-else @click="goToPage(pageNum)"
                  :class="pageNum === currentPage ? 'bg-yellow-50 dark:bg-gray-700 text-yellow-600 dark:text-white font-semibold' : 'hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white'"
                  class="flex items-center justify-center text-sm py-1.5 px-3 leading-tight text-gray-500 bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                  {{ pageNum }}
                </button>
              </li>
              <li>
                <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= lastPage"
                  class="flex items-center justify-center h-full py-1.5 px-2.5 leading-tight text-gray-500 bg-white rounded-r-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-40">
                  <span class="sr-only">Next</span>
                  <svg class="w-4 h-4" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/></svg>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showUserTypeModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div class="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Confirm User Type Override</h3>
        <p class="mt-3 text-sm text-gray-700 dark:text-gray-300">
          You changed the user type to <strong>{{ pendingUserTypeValue }}</strong>. Should this override be permanent?
        </p>
        <div class="mt-6 flex flex-wrap gap-3 justify-end">
          <button type="button" @click="confirmUserTypeOverride(true)" class="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700">Yes (Permanent)</button>
          <button type="button" @click="confirmUserTypeOverride(false)" class="px-4 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600">No</button>
          <button type="button" @click="cancelUserTypeOverride" class="px-4 py-2 rounded-lg bg-gray-400 text-white hover:bg-gray-500">Cancel</button>
        </div>
      </div>
    </div>

    <UserEditHistoryModal
      :is-visible="historyOpen"
      :user-id="historyUserId"
      @close="historyOpen = false"
    />
    <NewUserModal
      :is-visible="newUserOpen"
      @close="newUserOpen = false"
      @created="onUserCreated"
    />
  </div>
</template>

<script>
import axios from 'axios';
import UserEditHistoryModal from './modals/UserEditHistoryModal.vue';
import NewUserModal from './modals/NewUserModal.vue';

export default {
  components: { UserEditHistoryModal, NewUserModal },
  data() {
    return {
      rows: [],
      loading: false,
      total: 0,
      currentPage: 1,
      lastPage: 1,
      perPage: 25,
      search: '',
      status: 'active',
      scope: 'all',
      sortBy: 'name_first',
      sortDir: 'asc',
      statusOptions: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'all', label: 'All' },
      ],
      scopeOptions: [
        { value: 'all', label: 'All' },
        { value: 'internal', label: 'Internal' },
        { value: 'external', label: 'External' },
      ],
      historyOpen: false,
      historyUserId: null,
      newUserOpen: false,
      searchTimer: null,
      companyId: null,
      companyName: '',
      companySearch: '',
      companyOptions: [],
      companyDropdownOpen: false,
      companyHighlight: -1,
      roleId: null,
      roleOptions: [],
      // current admin's roles — gate the inline user-type override (10/20/30 only)
      userroles: [],
      userTypeChoices: [
        { label: 'Standard', value: 'standard' },
        { label: 'Cloud', value: 'cloud' },
        { label: 'Non-billable', value: 'non-billable' },
      ],
      showUserTypeModal: false,
      pendingUserTypeRow: null,
      pendingUserTypeOldValue: null,
      pendingUserTypeValue: null,
      togglingActiveId: null,
    };
  },
  computed: {
    canOverrideUserType() {
      return this.userroles.some(r => [10, 20, 30].includes(Number(r)));
    },
    visiblePages() {
      // Windowed page numbers with ellipses — same shape as the Tickets Summary pager.
      const pages = [];
      const total = this.lastPage;
      const cur = this.currentPage;
      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else if (cur <= 3) {
        pages.push(1, 2, 3, 4, '...', total);
      } else if (cur > total - 3) {
        pages.push(1, '...', total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', cur - 1, cur, cur + 1, '...', total);
      }
      return pages;
    },
    activeChips() {
      const chips = [];
      if (this.status !== 'all') chips.push({ key: 'status', label: `Status: ${this.statusLabel(this.status)}`, clear: () => this.setStatus('all') });
      if (this.scope !== 'all') chips.push({ key: 'scope', label: `Type: ${this.scopeLabel(this.scope)}`, clear: () => this.setScope('all') });
      if (this.search) chips.push({ key: 'q', label: `Search: ${this.search}`, clear: () => { this.search = ''; this.fetchPage(); } });
      if (this.companyId) chips.push({ key: 'company', label: `Company: ${this.companyName}`, clear: () => this.clearCompany() });
      if (this.roleId) {
        const name = this.roleOptions.find(r => r.id === this.roleId)?.name || `#${this.roleId}`;
        chips.push({ key: 'role', label: `Role: ${name}`, clear: () => { this.roleId = null; this.currentPage = 1; this.fetchPage(); } });
      }
      return chips;
    },
    filteredCompanies() {
      const q = (this.companySearch || '').trim().toLowerCase();
      if (!q) return this.companyOptions.slice(0, 50);
      return this.companyOptions.filter(c => (c.companyname || '').toLowerCase().includes(q)).slice(0, 50);
    },
  },
  mounted() {
    this.restoreFilters();
    this.loadRoles();
    this.loadCompanies();
    this.loadUserRoles();
    this.fetchPage();
  },
  methods: {
    // ---- Persistent filters (survive navigating to a user and back) ----
    saveFilters() {
      try {
        localStorage.setItem('userAdminFilters', JSON.stringify({
          search: this.search,
          status: this.status,
          scope: this.scope,
          companyId: this.companyId,
          companyName: this.companyName,
          companySearch: this.companySearch,
          roleId: this.roleId,
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
        }));
      } catch (e) { /* ignore */ }
    },
    restoreFilters() {
      try {
        const raw = localStorage.getItem('userAdminFilters');
        if (!raw) return;
        const f = JSON.parse(raw);
        if (!f || typeof f !== 'object') return;
        const keys = ['search', 'status', 'scope', 'companyId', 'companyName',
          'companySearch', 'roleId', 'currentPage', 'perPage', 'sortBy', 'sortDir'];
        keys.forEach(k => { if (f[k] !== undefined && f[k] !== null) this[k] = f[k]; });
      } catch (e) { /* ignore */ }
    },
    async loadUserRoles() {
      try {
        const res = await axios.get(`user/role/${localStorage.getItem('user_id')}`);
        this.userroles = Array.isArray(res.data?.roles) ? res.data.roles : [];
      } catch (e) {
        this.userroles = [];
      }
    },
    // ---- Inline user-type override (mirrors Assets.vue) ----
    handleUserTypeChange(row, event) {
      const newValue = event.target.value;
      if (!newValue || newValue === row.user_type) return;
      this.pendingUserTypeRow = row;
      this.pendingUserTypeOldValue = row.user_type;
      this.pendingUserTypeValue = newValue;
      this.showUserTypeModal = true;
    },
    closeUserTypeModal() {
      this.showUserTypeModal = false;
      this.pendingUserTypeRow = null;
      this.pendingUserTypeOldValue = null;
      this.pendingUserTypeValue = null;
    },
    cancelUserTypeOverride() {
      // Revert the <select> to the previous value (it's bound via :value, so refetch state)
      this.closeUserTypeModal();
    },
    async confirmUserTypeOverride(permanent) {
      if (!this.pendingUserTypeRow || !this.pendingUserTypeValue) {
        this.closeUserTypeModal();
        return;
      }
      this.$updateLoading(1);
      try {
        const rowId = this.pendingUserTypeRow.id;
        const res = await axios.post(`requesters/user-type/${rowId}`, {
          user_type: this.pendingUserTypeValue,
          permanent: permanent ? 1 : 0,
        });
        const payload = res.data?.data || {};
        this.pendingUserTypeRow.user_type = payload.user_type || this.pendingUserTypeValue;
        this.pendingUserTypeRow.user_type_overridden = payload.user_type_overridden ?? 1;
        this.pendingUserTypeRow.user_type_override_permanent = payload.user_type_override_permanent ?? (permanent ? 1 : 0);
      } catch (e) {
        alert(e?.response?.data?.message || 'Failed to update user type.');
      } finally {
        this.closeUserTypeModal();
        this.$updateLoading(-1);
        // Refetch so the bound <select> reflects the authoritative server state
        this.fetchPage();
      }
    },
    // ---- Active toggle (enable / disable account) ----
    async toggleActive(row, makeActive) {
      if (!this.canManageUser(row)) {
        alert('You can only change the status of users at or below your own privilege level.');
        this.fetchPage();
        return;
      }
      const verb = makeActive ? 'enable' : 'disable';
      const msg = makeActive
        ? `Re-enable ${this.displayName(row)}? They will be able to sign in again.`
        : `Disable ${this.displayName(row)}? They will no longer be able to sign in and existing sessions are revoked.`;
      if (!confirm(msg)) {
        // Revert the toggle visually by refetching the row's true state
        this.fetchPage();
        return;
      }
      this.togglingActiveId = row.id;
      this.$updateLoading(1);
      try {
        await axios.post(`users/${row.id}/${verb}`);
        row.retired = makeActive ? 0 : 1;
      } catch (e) {
        alert(e?.response?.data?.message || `Failed to ${verb} account.`);
        this.fetchPage();
      } finally {
        this.togglingActiveId = null;
        this.$updateLoading(-1);
      }
    },
    async fetchPage() {
      this.saveFilters();
      this.loading = true;
      this.$updateLoading(1);
      try {
        const res = await axios.get('admin/users', {
          params: {
            page:       this.currentPage,
            per_page:   this.perPage,
            status:     this.status,
            scope:      this.scope,
            q:          this.search,
            sort_by:    this.sortBy,
            sort_dir:   this.sortDir,
            company_id: this.companyId || undefined,
            role_ids:   this.roleId || undefined,
          },
        });
        this.rows         = Array.isArray(res.data?.data) ? res.data.data : [];
        this.total        = res.data?.total ?? this.rows.length;
        this.lastPage     = res.data?.last_page ?? 1;
        this.currentPage  = res.data?.current_page ?? 1;
      } catch (e) {
        console.error('Failed to load users', e);
        this.rows = [];
        this.total = 0;
        this.lastPage = 1;
      } finally {
        this.loading = false;
        this.$updateLoading(-1);
      }
    },
    displayName(u) {
      const full = `${u.name_first || ''} ${u.name_last || ''}`.trim();
      if (full) return full;
      const email = (u.email || '').trim();
      if (!email) return `User #${u.id}`;
      // Fall back to email's local part for role/shared mailboxes like accounting@…
      const localPart = email.split('@')[0] || email;
      return localPart;
    },
    isAzureSourced(u) {
      // Mirror the Assets.vue Source column: any non-empty azure_id means the user
      // was provisioned via Azure / Entra ID; otherwise treat the record as manual.
      return !!(u && u.azure_id && String(u.azure_id).trim() !== '');
    },
    debouncedSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.fetchPage();
      }, 300);
    },
    setStatus(v) {
      this.status = v;
      this.currentPage = 1;
      this.fetchPage();
    },
    setScope(v) {
      this.scope = v;
      this.currentPage = 1;
      this.fetchPage();
    },
    clearFilters() {
      this.search = '';
      this.status = 'all';
      this.scope = 'all';
      this.companyId = null;
      this.companyName = '';
      this.companySearch = '';
      this.companyDropdownOpen = false;
      this.companyHighlight = -1;
      this.roleId = null;
      this.currentPage = 1;
      this.fetchPage();
    },
    async loadCompanies() {
      try {
        const res = await axios.get('admin/companies');
        this.companyOptions = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.companyOptions = [];
      }
    },
    pickCompany(c) {
      this.companyId = c.id;
      this.companyName = c.companyname;
      this.companySearch = c.companyname;
      this.companyDropdownOpen = false;
      this.companyHighlight = -1;
      this.currentPage = 1;
      this.fetchPage();
    },
    clearCompany() {
      this.companyId = null;
      this.companyName = '';
      this.companySearch = '';
      this.companyDropdownOpen = false;
      this.companyHighlight = -1;
      this.currentPage = 1;
      this.fetchPage();
    },
    closeCompanyDropdown() {
      this.companyDropdownOpen = false;
      this.companyHighlight = -1;
    },
    onCompanyKey(direction) {
      if (!this.companyDropdownOpen) this.companyDropdownOpen = true;
      const list = this.filteredCompanies;
      if (!list.length) return;
      if (direction === 'down') {
        this.companyHighlight = (this.companyHighlight + 1) % list.length;
      } else if (direction === 'up') {
        this.companyHighlight = (this.companyHighlight - 1 + list.length) % list.length;
      }
      this.$nextTick(() => {
        const ul = this.$refs.companyList;
        const active = ul?.children?.[this.companyHighlight];
        active?.scrollIntoView({ block: 'nearest' });
      });
    },
    onCompanyEnter() {
      const list = this.filteredCompanies;
      if (this.companyHighlight >= 0 && this.companyHighlight < list.length) {
        this.pickCompany(list[this.companyHighlight]);
      } else if (list.length === 1) {
        this.pickCompany(list[0]);
      }
    },
    onRoleChange() {
      this.currentPage = 1;
      this.fetchPage();
    },
    async loadRoles() {
      try {
        const res = await axios.get('admin/roles');
        this.roleOptions = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.roleOptions = [];
      }
    },
    goToPage(p) {
      this.currentPage = p;
      this.fetchPage();
    },
    openHistory(userId) {
      this.historyUserId = userId;
      this.historyOpen = true;
    },
    async onUserCreated(payload) {
      this.newUserOpen = false;
      await this.fetchPage();
      if (payload?.id) this.$router.push(`/admin/users/${payload.id}`);
    },
    async exportCsv() {
      // Pull the full filtered set in one shot (large per_page) for the CSV.
      this.$updateLoading(1);
      try {
        const res = await axios.get('admin/users', {
          params: {
            page:     1,
            per_page: 100,            // backend caps at 100 — that's fine for paginated; for full export
            status:     this.status,
            scope:      this.scope,
            q:          this.search,
            sort_by:    this.sortBy,
            sort_dir:   this.sortDir,
            company_id: this.companyId || undefined,
            role_ids:   this.roleId || undefined,
          },
        });
        const total = res.data?.total ?? 0;
        let data = Array.isArray(res.data?.data) ? res.data.data : [];

        // If total exceeds first page, fetch the remaining pages.
        if (total > data.length) {
          const lastPage = res.data?.last_page ?? Math.ceil(total / 100);
          const promises = [];
          for (let p = 2; p <= lastPage; p++) {
            promises.push(axios.get('admin/users', {
              params: {
                page: p, per_page: 100,
                status: this.status, scope: this.scope, q: this.search,
                sort_by: this.sortBy, sort_dir: this.sortDir,
                company_id: this.companyId || undefined,
                role_ids: this.roleId || undefined,
              },
            }));
          }
          const pages = await Promise.all(promises);
          for (const r of pages) {
            if (Array.isArray(r.data?.data)) data = data.concat(r.data.data);
          }
        }

        const headers = ['ID', 'First name', 'Last name', 'Email', 'Phone primary', 'Phone primary ext', 'Phone secondary', 'Phone secondary ext', 'User type', 'Source', 'Scope', 'Status'];
        const rows = data.map(u => [
          u.id,
          u.name_first || '',
          u.name_last || '',
          u.email || '',
          u.phone_primary || '',
          u.phone_primary_ext || '',
          u.phone_secondary || '',
          u.phone_secondary_ext || '',
          u.user_type || '',
          this.isAzureSourced(u) ? 'Azure' : 'Manual',
          u.is_internal ? 'Internal' : 'External',
          u.retired ? 'Inactive' : 'Active',
        ]);
        const escape = (v) => {
          const s = (v == null ? '' : String(v)).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        };
        const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Export failed', e);
      } finally {
        this.$updateLoading(-1);
      }
    },
    // An admin may only manage users at or below their own privilege level (a user's
    // level = smallest/most-senior active role id they hold; lower id = more senior).
    canManageUser(u) {
      const ids = (u.roles || []).map(r => Number(r.id)).filter(n => !isNaN(n));
      const targetMin = ids.length ? Math.min(...ids) : 9999; // no roles = least senior
      const mine = (this.userroles || []).map(Number).filter(n => !isNaN(n));
      if (!mine.length) return false;
      return targetMin >= Math.min(...mine);
    },
    formatDateTime(value) {
      if (!value) return 'Never';
      // Stored in UTC. A bare MySQL datetime ("YYYY-MM-DD HH:MM:SS") carries no zone
      // and would be parsed as local time — normalize to UTC, then render in the
      // browser's local timezone. ISO strings that already carry a zone pass through.
      let s = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(s)) {
        s = s.replace(' ', 'T') + 'Z';
      }
      const d = new Date(s);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
    fmtPhone(raw) {
      const digits = String(raw || '').replace(/\D/g, '');
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      if (digits.length === 11 && digits[0] === '1') {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      }
      // Non-standard length — show the original as-is rather than mangle it.
      return String(raw || '').trim();
    },
    telLink(raw, ext) {
      const digits = String(raw || '').replace(/\D/g, '');
      if (!digits) return '';
      let href = 'tel:' + digits;
      const e = String(ext || '').replace(/\D/g, '');
      if (e) href += ';ext=' + e;
      return href;
    },
    statusLabel(v) { return this.statusOptions.find(o => o.value === v)?.label || v; },
    scopeLabel(v) { return this.scopeOptions.find(o => o.value === v)?.label || v; },
  },
};
</script>
